import BrandsSettings, {
  type BrandsSettingsDetailTab,
  type BrandsSettingsPageState,
} from "@/components/Settings/components/BrandsSettings";
import { isSingleBusinessGroupPlan } from "@/components/Settings/components/brandGroupEntitlement";
import { useAuth } from "@/contexts/AuthContext";
import { useBrandGroup } from "@/contexts/BrandGroupContext";
import { getAdminProductFeatures } from "@/utils/adminProductFeatures";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  buildBrandDetailPath,
  buildBrandWorkspacePath,
  parseBrandDetailTab,
  resolveSingleBusinessGroupDetailPath,
  type BrandDetailTab,
} from "./brandWorkspaceRoute";

const toSettingsTab = (tab: BrandDetailTab): BrandsSettingsDetailTab =>
  tab === "users" ? "members" : tab;

const toRouteTab = (tab: BrandsSettingsDetailTab): BrandDetailTab =>
  tab === "members" ? "users" : tab;

const BrandSettingsRoutePage = () => {
  const { brandId = "" } = useParams<{ brandId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { userBilling, currentWorkspace } = useAuth();
  const {
    feature: businessGroupFeature,
    data: businessGroupData,
    isLoading: businessGroupsLoading,
    error: businessGroupsError,
  } = useBrandGroup();
  const initialTab = toSettingsTab(parseBrandDetailTab(location.search));
  const businessGroupPlanCode =
    userBilling.summary?.planCode ||
    currentWorkspace?.subscription?.plan_code ||
    userBilling.userPlan?.planCode;

  if (!getAdminProductFeatures().brandManagement) {
    return <Navigate replace to="/workspace-settings" />;
  }

  const singleBusinessGroupDetailPath = resolveSingleBusinessGroupDetailPath({
    dataState:
      businessGroupsLoading ||
      userBilling.loading.plan ||
      businessGroupFeature === null
        ? "loading"
        : businessGroupsError
          ? "error"
          : "ready",
    isSingleGroupPlan: isSingleBusinessGroupPlan({
      planCode: businessGroupPlanCode,
      featureMaxBrands: businessGroupFeature?.maxBrands,
    }),
    businessGroupIds: businessGroupData.brands.map(
      (businessGroup) => businessGroup.id,
    ),
    requestedBusinessGroupId: brandId || null,
    tab: parseBrandDetailTab(location.search),
  });
  if (singleBusinessGroupDetailPath) {
    return <Navigate replace to={singleBusinessGroupDetailPath} />;
  }

  const handlePageChange = (page: BrandsSettingsPageState) => {
    if (page.kind === "directory") {
      navigate(buildBrandWorkspacePath());
      return;
    }

    navigate(buildBrandDetailPath(page.brandId, toRouteTab(page.tab)));
  };

  return (
    <div className="h-full min-w-0 flex-1 overflow-y-auto bg-[#F4F5F8] [scrollbar-gutter:stable]">
      <main className="mx-auto w-full max-w-[1280px] px-6 py-8 pb-16">
        <BrandsSettings
          initialBrandId={brandId || null}
          initialTab={initialTab}
          onPageChange={handlePageChange}
        />
      </main>
    </div>
  );
};

export default BrandSettingsRoutePage;
